import { FC, useState } from 'react';
import { InfoOutlined } from '@mui/icons-material';
import { Skeleton, Tooltip, Typography } from '@mui/material';
import moment from 'moment';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import useGetCompanyKeyDevelopments from 'services/hooks/companies/useGetCompanyKeyDevelopments';
import {
  CategoryChips,
  ChipButton,
  ExpandableText,
  ExpandToggle,
  ItemSource,
  MetaDot,
  NewsItem,
  NewsList,
  NewsMeta,
  NewsTitle,
  TransactionBadge,
  TransactionIdCode,
} from '../NewsAndPressReleasesCard.styles';
import { ALL_CATEGORIES, DEFAULT_CATEGORIES, KEY_DEV_CATEGORY_LABELS } from './CapIQKeyDevelopmentsCard.constants';
import { CapIQKeyDevelopmentsCardProps, SituationItemProps, TransactionMetaProps } from './CapIQKeyDevelopmentsCard.props';

const TransactionMeta: FC<TransactionMetaProps> = ({ transactionId, type }) => {
  const [copied, setCopied] = useState(false);
  if (!transactionId && !type) return null;

  const handleCopy = () => {
    if (!transactionId) return;
    navigator.clipboard.writeText(transactionId);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const tooltipContent = (
    <div>
      {type && <div>Type: {type}</div>}
      {transactionId && (
        <div>
          ID: <TransactionIdCode>{transactionId}</TransactionIdCode>
          {' '}(click to copy)
        </div>
      )}
      {copied && <div style={{ color: '#4caf50', marginTop: 2 }}>Copied!</div>}
    </div>
  );

  return (
    <Tooltip title={tooltipContent} placement="top" arrow>
      <TransactionBadge onClick={handleCopy}>
        <InfoOutlined sx={{ fontSize: '0.7rem' }} />
      </TransactionBadge>
    </Tooltip>
  );
};

const SituationItem: FC<SituationItemProps> = ({ situation }) => {
  const [expanded, setExpanded] = useState(false);
  if (!situation) {
    return null;
  }
  return (
    <>
      <ExpandableText expanded={expanded}>{situation}</ExpandableText>
      <ExpandToggle onClick={() => setExpanded(e => !e)}>
        {expanded ? 'Show less' : 'Show more'}
      </ExpandToggle>
    </>
  );
};

const CapIQKeyDevelopmentsCard: FC<CapIQKeyDevelopmentsCardProps> = ({ companyId }) => {
  const [selectedCategories, setSelectedCategories] = useState<string[]>(DEFAULT_CATEGORIES);
  const debouncedCategories = useDebouncedValue(selectedCategories, 1000);

  const { items, isLoading, isFetching } = useGetCompanyKeyDevelopments(companyId, debouncedCategories);
  const busy = isLoading || isFetching;
  const hasData = items.length > 0;

  const toggleCategory = (cat: string) => {
    setSelectedCategories(prev => {
      if (prev.includes(cat)) {
        const next = prev.filter(c => c !== cat);
        // Don't allow deselecting all
        return next.length > 0 ? next : prev;
      }
      return [...prev, cat];
    });
  };

  if (busy) {
    return (
      <NewsList>
        {[0, 1, 2, 3].map(i => (
          <NewsItem key={i}>
            <Skeleton variant="text" width="90%" height={16} />
            <Skeleton variant="text" width="40%" height={12} />
            <Skeleton variant="text" width="100%" height={12} />
          </NewsItem>
        ))}
      </NewsList>
    );
  }

  return (
    <>
      <CategoryChips>
        {ALL_CATEGORIES.map(cat => (
          <ChipButton
            key={cat}
            active={selectedCategories.includes(cat)}
            onClick={() => toggleCategory(cat)}
          >
            {KEY_DEV_CATEGORY_LABELS[cat]}
          </ChipButton>
        ))}
      </CategoryChips>

      {!hasData && (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 3 }}>
          No key developments available for this company.
        </Typography>
      )}

      {hasData && <NewsList>
        {items.map((item, i) => (
          <NewsItem key={i}>
            <NewsTitle as="span">{item.headline}</NewsTitle>
            <NewsMeta>
              {item.date && (
                <span>
                  {moment(item.date, 'MMM DD YYYY hh:mmA').isValid()
                    ? moment(item.date, 'MMM DD YYYY hh:mmA').format('MMM D, YYYY')
                    : item.date}
                </span>
              )}
              {item.source && (
                <>
                  <MetaDot> · </MetaDot>
                  <ItemSource>{item.source}</ItemSource>
                </>
              )}
              {(item.transaction_id || item.type) && (
                <>
                  <MetaDot> · </MetaDot>
                  <TransactionMeta transactionId={item.transaction_id ?? null} type={item.type ?? null} />
                </>
              )}
            </NewsMeta>
            {item.situation && <SituationItem situation={item.situation} />}
          </NewsItem>
        ))}
      </NewsList>}
    </>
  );
};

export default CapIQKeyDevelopmentsCard;
